/**
 * One traffic source (/traffic/:id, slug or id): its numbers for the period,
 * the trend, the people it brought, what the channel itself reports (ads,
 * followers, affiliates), the work, the playbook and how it is tracked.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, RefreshCw, Route } from "lucide-react";
import { Button, Skeleton, useConfirm, useToast } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { backFromState } from "@/lib/back-target";
import { Card, Empty } from "@/features/command/ui";
import { errMsg, useTeam } from "@/features/traffic-sources/shared";
import { DetailHeader } from "@/features/traffic-sources/detail/Header";
import { KpiTiles, TrendCard } from "@/features/traffic-sources/detail/Numbers";
import { ArrivalsSection, PeopleSection } from "@/features/traffic-sources/detail/People";
import { AdsSection, AffiliatesSection, FollowersSection } from "@/features/traffic-sources/detail/Channels";
import { WorkSection } from "@/features/traffic-sources/detail/Work";
import { BreakdownSection } from "@/features/traffic-sources/detail/Breakdown";
import { PlaybookSection, TrackingSection } from "@/features/traffic-sources/detail/Playbook";
import type { DetailResponse, HubResponse, HubSource, SourceRow, TaskCard } from "@/features/traffic-sources/types";
import { trackRecent } from "@/lib/recent";

const PERIODS = [7, 30, 90];
// Fields the server accepts on PATCH /sources/:id (owner_name is display only).
const PATCHABLE = ["name", "parent_id", "kind", "status", "owner_id", "description", "playbook", "match", "monthly_cost_usd", "color", "icon"] as const;

export function SourceDetailPage() {
  const { id = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const team = useTeam();
  const [params, setParams] = useSearchParams();
  const days = PERIODS.includes(Number(params.get("days"))) ? Number(params.get("days")) : 30;
  const [data, setData] = useState<DetailResponse | null>(null);
  const [missing, setMissing] = useState(false);
  const [failed, setFailed] = useState(false);
  const [childNums, setChildNums] = useState<Record<string, HubSource> | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const back = backFromState(location.state, { to: "/traffic", label: "Traffic" });
  const from = location.pathname + location.search;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get<DetailResponse>(`/sources/${encodeURIComponent(id)}`, { days });
      setData(d);
      trackRecent({ to: `/traffic/${d.source.slug}`, label: d.source.name, kind: "source" });
      setMissing(false);
      setFailed(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setMissing(true);
      else { setFailed(true); toast.error("Couldn't load that source", errMsg(e)); }
    } finally {
      setLoading(false);
    }
  }, [id, days, toast]);

  useEffect(() => { void load(); }, [load]);

  // Sub-sources side by side: their numbers come from the hub list.
  const kidKey = data?.children.map((c) => c.id).join(",") ?? "";
  useEffect(() => {
    if (!kidKey) { setChildNums(null); return; }
    let live = true;
    api.get<HubResponse>("/sources", { days })
      .then((r) => { if (live) setChildNums(Object.fromEntries(r.sources.map((x) => [x.id, x]))); })
      .catch(() => { if (live) setChildNums({}); });
    return () => { live = false; };
  }, [kidKey, days]);
  // A different source: don't show the old one's numbers while the new one loads.
  useEffect(() => { setData((d) => (d && d.source.slug !== id && d.source.id !== id ? null : d)); }, [id]);

  const setDays = (d: number) => {
    const next = new URLSearchParams(params);
    if (d === 30) next.delete("days"); else next.set("days", String(d));
    setParams(next, { replace: true, state: location.state });
  };

  const patch = useCallback(async (body: Partial<SourceRow>): Promise<boolean> => {
    if (!data) return false;
    const before = data.source;
    setData((d) => d && { ...d, source: { ...d.source, ...body } });
    const apiBody: Record<string, unknown> = {};
    for (const k of PATCHABLE) if (k in body) apiBody[k] = body[k];
    try {
      const r = await api.patch<{ source: SourceRow }>(`/sources/${before.id}`, apiBody);
      setData((d) => d && { ...d, source: { ...d.source, ...r.source, owner_name: "owner_name" in body ? body.owner_name ?? null : d.source.owner_name } });
      // The kind decides which channel sections the server sends (ads, followers, affiliates).
      if ("kind" in body || "parent_id" in body) void load();
      return true;
    } catch (e) {
      setData((d) => d && { ...d, source: before });
      toast.error("Couldn't save that", errMsg(e));
      return false;
    }
  }, [data, toast, load]);

  const archive = async () => {
    if (!data) return;
    const ok = await confirm({
      title: `Archive ${data.source.name}?`,
      description: "It leaves the traffic page. People it brought keep their history; new arrivals get credited elsewhere.",
      confirmLabel: "Archive",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/sources/${data.source.id}`);
      toast.success(`Archived ${data.source.name}`);
      navigate("/traffic");
    } catch (e) {
      toast.error("Couldn't archive it", errMsg(e));
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post<{ ads: { configured?: boolean; errors?: string[] }; social: { configured?: boolean; errors?: string[] } }>("/sources/sync");
      if (r.ads?.configured === false && r.social?.configured === false) {
        toast.info("Sync isn't set up here", "This CRM has no SyncSuite key to read ads and followers with.");
      } else {
        const errs = [...(r.ads?.errors ?? []), ...(r.social?.errors ?? [])];
        if (errs.length) toast.error("Synced with problems", errs.slice(0, 3).join(" · "));
        else toast.success("Synced");
      }
      await load();
    } catch (e) {
      toast.error("Sync failed", errMsg(e));
    } finally {
      setSyncing(false);
    }
  };

  const setTasks = useCallback((fn: (t: TaskCard[]) => TaskCard[]) => setData((d) => d && { ...d, tasks: fn(d.tasks) }), []);

  if (missing) {
    return (
      <div className="space-y-4">
        <Link to={back.to} className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-400 hover:text-zinc-100"><ArrowLeft className="h-3.5 w-3.5" /> {back.label}</Link>
        <Card>
          <Empty icon={Route} title="That source isn't here" action={<Button size="sm" variant="secondary" onClick={() => navigate("/traffic")}>See every source</Button>}>
            It may have been archived or renamed.
          </Empty>
        </Card>
      </div>
    );
  }

  if (!data && failed) {
    return (
      <div className="space-y-4">
        <Link to={back.to} className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-400 hover:text-zinc-100"><ArrowLeft className="h-3.5 w-3.5" /> {back.label}</Link>
        <Card>
          <Empty icon={Route} title="Couldn't load this source" action={<Button size="sm" variant="secondary" loading={loading} onClick={() => void load()} leftIcon={<RefreshCw className="h-3.5 w-3.5" />}>Try again</Button>}>
            The server didn't answer. Try again in a moment.
          </Empty>
        </Card>
      </div>
    );
  }

  if (!data) return <DetailSkeleton />;

  const s = data.source;
  const kind = s.kind;
  const showAds = kind === "paid" || data.campaigns.length > 0;
  const showFollowers = !!data.social;
  const showAffiliates = data.affiliates.length > 0 || kind === "affiliate" || kind === "partner";

  return (
    <div className={loading ? "space-y-5 opacity-80 transition-opacity" : "space-y-5 transition-opacity"}>
      <DetailHeader data={data} back={back} days={days} onDays={setDays} onPatch={patch} onArchive={() => void archive()} team={team} from={from} />

      <AreaLink kind={kind} slug={s.slug} name={s.name} />

      <KpiTiles now={data.numbers.now} prev={data.numbers.prev} totals={data.numbers.totals} days={days} />
      <TrendCard series={data.series} days={days} />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        <div className="min-w-0 space-y-5 lg:col-span-3">
          {data.children.length > 0 && <BreakdownSection kids={data.children} nums={childNums} days={days} from={from} />}
          {showAds && <AdsSection ads={data.ads} campaigns={data.campaigns} onSync={() => void sync()} syncing={syncing} />}
          {showFollowers && data.social && <FollowersSection social={data.social} onSync={() => void sync()} syncing={syncing} />}
          {showAffiliates && <AffiliatesSection affiliates={data.affiliates} from={from} />}
          <PeopleSection people={data.people} sourceId={s.id} from={from} />
          {data.touches.length > 0 && <ArrivalsSection touches={data.touches} from={from} />}
        </div>
        <div className="min-w-0 space-y-5 lg:col-span-2">
          <WorkSection tasks={data.tasks} goals={data.goals} sourceId={s.id} onChanged={() => void load()} onTasks={setTasks} />
          <PlaybookSection source={s} onPatch={patch} />
          <TrackingSection source={s} onPatch={patch} onRecomputed={() => void load()} />
        </div>
      </div>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-4 w-20" />
      <div className="flex items-center gap-4">
        <Skeleton className="h-12 w-12 rounded-2xl" />
        <div className="space-y-2"><Skeleton className="h-7 w-64" /><Skeleton className="h-5 w-48" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {Array.from({ length: 8 }, (_, i) => <Skeleton key={i} className="h-[104px] rounded-2xl" />)}
      </div>
      <Skeleton className="h-64 rounded-2xl" />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-5">
        <Skeleton className="h-80 rounded-2xl lg:col-span-3" />
        <Skeleton className="h-80 rounded-2xl lg:col-span-2" />
      </div>
    </div>
  );
}

/** Kinds of traffic that have their own system under Growth (2026-10-28). */
const AREA_BY_KIND: Record<string, { to: string; label: string; what: string }> = {
  paid: { to: "/ads", label: "Ads", what: "every ad, credited down to the exact ad, with Gabi's 4PI" },
  outreach: { to: "/outreach", label: "Outreach", what: "who to message today, follow-ups and calls" },
  partner: { to: "/partners", label: "Partners", what: "every partner's rank and today's touches" },
  affiliate: { to: "/affiliates", label: "Affiliates", what: "the program: leaderboard, payouts, assets and recruiting" },
  ugc: { to: "/creators", label: "Creators", what: "briefs, clips, views and pay per creator" },
  organic: { to: "/content", label: "Social", what: "the posting planner and which posts led to signups" },
};

function AreaLink({ kind, slug, name }: { kind: string; slug: string; name: string }) {
  const area = slug === "agency-handoffs" ? null : AREA_BY_KIND[kind];
  if (!area) return null;
  return (
    <Link to={area.to} className="cc-card cc-card-hover flex items-center gap-3 px-4 py-3">
      <span className="min-w-0 flex-1 text-sm text-zinc-300">
        <span className="font-medium text-zinc-100">{name}</span> is run from {area.label}: {area.what}.
      </span>
      <span className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-indigo-300">Open {area.label} <ArrowRight className="h-3.5 w-3.5" /></span>
    </Link>
  );
}
